import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BrandMark } from "../components/Brand";
import { api, formatCurrency, formatDate, formatApiError, openAuthedPdf } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { STATUS_STYLES } from "../components/ops/opsUtils";
import ImpersonationBanner from "../components/ImpersonationBanner";
import { getAttorney } from "../lib/attorneyAuth";
import SettlementCalculator from "../components/SettlementCalculator";
import {
  ArrowLeft, Receipt, FileSignature, Loader2, ExternalLink, Activity, FileText, ChevronRight,
  Users, Calculator, Layers, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

export default function AttorneyClientDetail() {
  const { client_key } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [calcOpen, setCalcOpen] = useState(true);
  useSEO(data ? `${data.client_name} · Client` : "Client", "Unified client detail in the Instabridge firm portal.");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client_key]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/clients/${client_key}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/attorney-login", { replace: true });
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const generateCombinedPayoff = async () => {
    setGenerating(true);
    try {
      const { data: res } = await api.post(`/portal/clients/${client_key}/quick-payoff`, { valid_days: 10 });
      toast.success(`Combined payoff ${res.statement_number} generated · covers ${res.loan_ids.length} advances`);
      await openAuthedPdf(`/portal/payoffs/${res.payoff_id}/pdf`, `${res.statement_number || res.payoff_id}.pdf`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const activeAdvances = useMemo(() => (data?.advances || []).filter(a =>
    a.current_status !== "Declined"
  ), [data]);
  // Only hide the calculator entirely if every advance is Declined (no underlying balance).
  const showCalculator = (data?.advances || []).some(a => a.current_status !== "Declined");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading client…</div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="attorney-client-detail">
      <ImpersonationBanner impersonation={(getAttorney() || {}).impersonation} firm={(getAttorney() || {}).firm} />
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="mx-auto max-w-[1300px] px-5 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/attorney/portal"><BrandMark /></Link>
          <Link to="/attorney/portal" className="text-xs text-[#6B7280] hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="client-back"><ArrowLeft className="w-3.5 h-3.5" /> Portal</Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1300px] px-5 lg:px-8 py-7 space-y-6">
        {/* Header card */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 lg:p-7">
          <div className="flex items-start justify-between flex-wrap gap-5">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
                <Users className="w-3.5 h-3.5" /> Client · {data.advance_count} advance{data.advance_count !== 1 ? "s" : ""}
              </div>
              <h1 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold">{data.client_name}</h1>
              <div className="text-xs text-[#6B7280] mt-2 tabular-nums">
                Combined total of every advance in your firm for this client.
              </div>
            </div>
            {activeAdvances.length > 0 && (
              <button onClick={generateCombinedPayoff} disabled={generating} data-testid="client-combined-payoff-btn"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0A2540] hover:bg-[#001A3D] text-white text-sm font-medium transition-colors disabled:opacity-60">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                Generate combined payoff
                <span className="text-[10px] bg-white/15 rounded-full px-2 py-0.5">{activeAdvances.length} active</span>
              </button>
            )}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6 pt-6 border-t border-[#F3F4F6]">
            <Stat label="Combined principal" value={formatCurrency(data.combined.total_principal)} />
            <Stat label="Combined payoff" value={formatCurrency(data.combined.total_payoff)} accent />
            <Stat label="Reductions applied" value={formatCurrency(data.combined.total_reductions)} />
            <Stat label="Advances" value={`${data.advance_count}`} />
          </div>
        </div>

        {/* Advances table */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid="client-advances">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-2 text-sm font-medium text-[#0A2540]">
            <FileSignature className="w-4 h-4 text-[#0066FF]" /> Advances
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] bg-[#F9FAFB]">
                  <th className="px-4 py-2.5 font-medium">Loan</th>
                  <th className="px-4 py-2.5 font-medium">Funded</th>
                  <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                  <th className="px-4 py-2.5 font-medium text-right">Rate</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium text-right">Current payoff</th>
                  <th className="px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {data.advances.map((a) => {
                  const st = STATUS_STYLES[a.current_status] || { bg: "#F3F4F6", text: "#374151", label: a.current_status };
                  return (
                    <tr key={a.loan_id} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`advance-${a.loan_id}`}>
                      <td className="px-4 py-3 text-xs text-[#0A2540] tabular-nums font-mono">{a.loan_id}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] tabular-nums">{formatDate(a.funding_date)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#0A2540]">{formatCurrency(a.principal)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#374151]">{a.rate_str}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: st.bg, color: st.text }}>{st.label}</span></td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0066FF]">{formatCurrency(a.current_payoff)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/attorney/portal/loans/${a.loan_id}`} className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#6B7280] hover:text-[#0A2540] hover:bg-[#F3F4F6]" data-testid={`view-advance-${a.loan_id}`}>
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-[#F9FAFB]">
                <tr>
                  <td colSpan={2} className="px-4 py-2.5 text-xs uppercase tracking-wider text-[#6B7280] font-medium">Combined totals</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[#0A2540]">{formatCurrency(data.combined.total_principal)}</td>
                  <td colSpan={2}></td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-[#0066FF]">{formatCurrency(data.combined.total_payoff)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Settlement calculator (collapsible) */}
        {showCalculator && (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid="settlement-calc-section">
          <button onClick={() => setCalcOpen((o) => !o)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-[#F9FAFB] text-left" data-testid="calc-toggle">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0066FF] to-[#001A3D] text-white flex items-center justify-center">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280]">Settlement distribution calculator</div>
                <div className="text-sm font-medium text-[#0A2540]">See attorney fee, Instabridge payoff, and estimated client net — instantly.</div>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-[#6B7280] transition-transform ${calcOpen ? "rotate-180" : ""}`} />
          </button>
          {calcOpen && (
            <div className="px-5 pb-6 pt-2 border-t border-[#F3F4F6]">
              <SettlementCalculator
                clientKey={client_key}
                clientName={data.client_name}
                instabridgePayoffLive={data.combined.total_payoff}
              />
            </div>
          )}
        </div>
        )}

        {/* Two-column lower section: documents + activity */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Section title="Agreements" icon={FileSignature} count={data.contracts.length} testid="client-contracts">
              {data.contracts.length === 0 ? (
                <EmptyRow label="No agreements on file for this client yet." />
              ) : data.contracts.map((c) => (
                <DocRow key={c.contract_id} title="Funding Agreement"
                  sub={`${c.loan_id} · ${c.status || "Draft"} · ${formatDate(c.created_at)}`}
                  apiPath={`/portal/contracts/${c.contract_id}/pdf`}
                  filename={`${c.contract_id}.pdf`}
                  testid={`client-contract-${c.contract_id}`} />
              ))}
            </Section>
            <Section title="Payoff statements" icon={Receipt} count={data.payoffs.length} testid="client-payoffs">
              {data.payoffs.length === 0 ? (
                <EmptyRow label="No payoff letters yet — click ‘Generate combined payoff’ above." />
              ) : data.payoffs.map((p) => (
                <DocRow key={p.payoff_id}
                  title={p.statement_number || p.payoff_id}
                  sub={`${formatCurrency(p.final_amount)} · valid through ${formatDate(p.valid_through)}`}
                  apiPath={`/portal/payoffs/${p.payoff_id}/pdf`}
                  filename={`${p.statement_number || p.payoff_id}.pdf`}
                  testid={`client-payoff-${p.payoff_id}`} />
              ))}
            </Section>
          </div>

          <div className="space-y-5">
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-4">
                <Activity className="w-3.5 h-3.5" /> Activity · all advances
              </div>
              {data.timeline.length === 0 ? (
                <div className="text-sm text-[#6B7280]">No activity recorded.</div>
              ) : (
                <ol className="relative border-l border-[#E5E7EB] ml-1 space-y-4" data-testid="client-timeline">
                  {data.timeline.slice(0, 30).map((ev, i) => (
                    <li key={i} className="ml-4">
                      <div className="absolute w-2 h-2 bg-[#0066FF] rounded-full -left-[5px] mt-1.5" />
                      <div className="text-xs text-[#0A2540] font-medium">{prettyKind(ev.kind)}</div>
                      <div className="text-[11px] text-[#6B7280] tabular-nums">{formatDate(ev.at)}{ev.loan_id ? ` · ${ev.loan_id}` : ""}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function prettyKind(k) {
  return ({
    status_change: "Status changed",
    payoff_generated: "Payoff generated",
    contract_created: "Agreement drafted",
    contract_signed: "Agreement signed",
    settlement_reported: "Settlement reported",
    funding_requested: "Additional funding requested",
    funding_request_decided: "Funding request decided",
  })[k] || (k || "").replace(/_/g, " ");
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className={`tabular-nums font-display ${accent ? "text-[#0066FF] text-2xl font-semibold" : "text-[#0A2540] text-xl font-medium"}`}>{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, count, children, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-[#0A2540]"><Icon className="w-4 h-4 text-[#0066FF]" /> {title}</div>
        <span className="text-xs text-[#6B7280] tabular-nums">{count}</span>
      </div>
      <div className="divide-y divide-[#F3F4F6]">{children}</div>
    </div>
  );
}

function DocRow({ title, sub, apiPath, filename, testid }) {
  const [busy, setBusy] = useState(false);
  const open = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try { await openAuthedPdf(apiPath, filename); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={open} disabled={busy} className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#F9FAFB] group disabled:opacity-60 text-left" data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#0A2540] truncate">{title}</div>
          <div className="text-xs text-[#6B7280] truncate">{sub}</div>
        </div>
      </div>
      {busy ? <Loader2 className="w-3.5 h-3.5 text-[#9CA3AF] animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#0066FF]" />}
    </button>
  );
}

function EmptyRow({ label }) {
  return <div className="px-5 py-6 text-center text-sm text-[#6B7280]">{label}</div>;
}
